import { useNavigate } from 'react-router-dom';
import { Card, Button } from '@/components/ui';
import { Calendar, Zap, ArrowUpCircle, ArrowDownCircle, Package } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import type { DailySummaryData } from './types';

interface DailySummaryProps {
  summary: DailySummaryData;
}

export function DailySummary({ summary }: DailySummaryProps) {
  const navigate = useNavigate();

  return (
    <Card
      padding="md"
      className="bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-900/30 dark:to-amber-900/30 border-orange-200 dark:border-orange-700"
    >
      <div className="flex items-center gap-2 mb-4">
        <div className="p-2 bg-orange-500 rounded-lg">
          <Calendar className="text-white" size={20} />
        </div>
        <div>
          <h3 className="font-semibold text-gray-900 dark:text-white text-sm sm:text-base">
            Résumé du jour
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {format(new Date(), 'EEEE d MMMM', { locale: fr })}
          </p>
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between p-2.5 bg-white/70 dark:bg-gray-800/70 rounded-lg">
          <div className="flex items-center gap-2">
            <Zap size={16} className="text-orange-500" />
            <span className="text-sm text-gray-700 dark:text-gray-300">Mouvements</span>
          </div>
          <span className="font-bold text-gray-900 dark:text-white">
            {summary.movementsToday}
          </span>
        </div>

        <div className="flex items-center justify-between p-2.5 bg-white/70 dark:bg-gray-800/70 rounded-lg">
          <div className="flex items-center gap-2">
            <ArrowUpCircle size={16} className="text-green-500" />
            <span className="text-sm text-gray-700 dark:text-gray-300">Entrées</span>
          </div>
          <span className="font-bold text-green-600">
            +{summary.entriesTotal.toFixed(1)}
          </span>
        </div>

        <div className="flex items-center justify-between p-2.5 bg-white/70 dark:bg-gray-800/70 rounded-lg">
          <div className="flex items-center gap-2">
            <ArrowDownCircle size={16} className="text-red-500" />
            <span className="text-sm text-gray-700 dark:text-gray-300">Sorties</span>
          </div>
          <span className="font-bold text-red-600">
            -{summary.exitsTotal.toFixed(1)}
          </span>
        </div>

        <div className="flex items-center justify-between p-2.5 bg-white/70 dark:bg-gray-800/70 rounded-lg">
          <div className="flex items-center gap-2">
            <Package size={16} className="text-blue-500" />
            <span className="text-sm text-gray-700 dark:text-gray-300">Produits</span>
          </div>
          <span className="font-bold text-gray-900 dark:text-white">
            {summary.productsUpdated}
          </span>
        </div>
      </div>

      {summary.movementsToday === 0 && (
        <div className="mt-3 pt-3 border-t border-orange-200 dark:border-orange-700 text-center">
          <p className="text-xs text-orange-600">Aucune activité aujourd'hui</p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/inventory-mode')}
            className="mt-2 text-xs"
          >
            Commencer l'inventaire
          </Button>
        </div>
      )}
    </Card>
  );
}
